<template>
  <div class="nx-navigation-selector-wrapper">

    <nx-relation-editor-toolbar :title="label">
      <template slot="actions">
        <v-tooltip top>
          <v-btn
              :loading="loading"
              @click="openWizardDialog"
              icon
              slot="activator"
              v-if="!internalValue">
            <v-icon>add</v-icon>
          </v-btn>
          <span>Crear nueva navegación</span>
        </v-tooltip>
      </template>
    </nx-relation-editor-toolbar>

    <v-layout v-if="!internalValue">
      <v-flex class="text-xs-center">
        <v-btn
            @click="openWizardDialog"
            :loading="loading"
            class="nx-text--level-1"
            flat small>
          <v-icon left>add</v-icon>
          Crear nueva navegación
        </v-btn>
      </v-flex>
    </v-layout>

    <v-list v-else>
      <v-list-tile>
        <v-list-tile-title v-html="internalValue.name"></v-list-tile-title>
        <v-list-tile-action>
          <v-layout row align-center ml-4>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  :to="entityEditorRoute.get('navigation', internalValue)"
                  class="nx-text--level-1 mr-2"
                  icon
                  target="_blank"
                  v-on="on">
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('message.common.edit') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="deleteNavigation(internalValue.id)"
                  :loading="loading"
                  class="nx-text--level-1"
                  icon
                  v-if="deletable"
                  v-on="on">
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('message.common.delete') }}</span>
            </v-tooltip>
          </v-layout>
        </v-list-tile-action>
      </v-list-tile>
    </v-list>

    <nx-dialog-confirm
      :text="$t('message.content.editor.deleteNavigationConfirmText')"
      :accept-text="$t('message.content.editor.deleteNavigationAcceptText')"
      :cancel-text="$t('message.content.editor.deleteNavigationCancelText')"
      accept-color="primary"
      cancel-color="primary"
      ref="confirm"
    ></nx-dialog-confirm>

    <nx-fullscreen-dialog
        v-if="wizardDialogState.render"
        :active="wizardDialogState.active"
        :title="$t('message.wizard.new.navigation')"
        dismissable
        @dismiss="closeWizardDialog"
    >

      <template slot="toolbar-items">
        <v-btn
            :disabled="!newNavigationName"
            @click.native="createNavigation"
            color="accent"
            :loading="loading"
            flat dark
        >Crear
        </v-btn>
      </template>

      <v-container class="px-0 pt-5 pb-0 fluid">
        <v-layout wrap>
          <v-flex class="md8 offset-md2 mb-3">
            <v-text-field
                :label="$t('message.form.fields.name')"
                v-model="newNavigationName"
            ></v-text-field>
          </v-flex>
        </v-layout>
      </v-container>

    </nx-fullscreen-dialog>
  </div>
</template>

<script>
import { entityEditorRoute } from '@/modules/entity/router';
import { isObject, isNumber, get } from 'lodash';
import RelationEditorToolbar from '@/modules/relation/editor/components/Toolbar';
import FullscreenDialog from '@/components/dialogs/FullscreenDialog';
import HasEntityFields from '@/modules/form/mixins/has-entity-fields';
import Confirm from '@/components/dialogs/Confirm';


import api from './api';

export default {
  name: 'nx-navigation-selector',
  mixins: [HasEntityFields],
  components: {
    [RelationEditorToolbar.name]: RelationEditorToolbar,
    [FullscreenDialog.name]: FullscreenDialog,
    [Confirm.name]: Confirm,
  },
  props: {
    value: {
      type: [Number, Object],
    },
    name: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Relación de navegación',
    },
    deletable: {
      type: Boolean,
      required: false,
      default: false,
    },
    maxDepth: {
      type: Number,
    },
    itemsType: {
      type: String,
    },
  },
  data: () => ({
    internalValue: null,
    wizardDialogState: { render: false, active: false },
    newNavigationName: '',
    loading: false,
  }),
  computed: {
    entityEditorRoute() {
      return entityEditorRoute;
    },
  },
  watch: {
    value: {
      async handler() {
        if (isObject(this.value)) this.internalValue = this.value;
        else if (isNumber(this.value)) this.internalValue = await api.fetch(this.value);
        else this.internalValue = null;
      },
      immediate: true,
    },
  },
  methods: {
    async createNavigation() {
      if (!this.newNavigationName) return;
      this.loading = true;

      try {
        this.internalValue = await api.create(this.getNavigationParams(this.newNavigationName));
        this.newNavigationName = '';
        this.$emit('input', this.internalValue);
        this.closeWizardDialog();
      } catch (e) {
        console.log('Error creating navigation', e);
      } finally {
        this.loading = false;
      }
    },
    async deleteNavigation(navigationId) {
      this.loading = true;
      const deleteNavigation = await this.$refs.confirm.open();
      if (deleteNavigation) {
        await api.delete(navigationId);
        this.internalValue = null;
        this.$emit('input', null);
      }
      this.loading = false;
    },
    closeWizardDialog() {
      this.wizardDialogState.active = false;
      this.$nextTick(() => {
        this.wizardDialogState.render = false;
      });
    },
    openWizardDialog() {
      this.newNavigationName = this.name;
      this.wizardDialogState.render = true;
      this.$nextTick(() => {
        this.wizardDialogState.active = true;
      });
    },
    getNavigationParams(name) {
      const params = { name };
      if (this.maxDepth) {
        params.maxDepth = this.maxDepth;
      }
      if (this.itemsType) {
        params.itemsType = this.itemsType;
      }
      if (this.entity && this.entityField && this.getEntityId()) {
        return {
          entity: this.entity,
          entityField: this.entityField,
          entityId: this.getEntityId(),
          ...params,
        };
      }
      return params;
    },
  },
};
</script>
